import React from "react";
import { useTheme, ThemeMode } from "theme";
import "./styles.css";

const OPTIONS: {
  value: ThemeMode;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    value: "light",
    label: "Light",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        <path
          fill="currentColor"
          d="M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12Zm0 3.75a.75.75 0 0 1-.75-.75v-1.5a.75.75 0 0 1 1.5 0v1.5a.75.75 0 0 1-.75.75Zm0-16.5a.75.75 0 0 1-.75-.75V3a.75.75 0 0 1 1.5 0v1.5a.75.75 0 0 1-.75.75Zm9 6.75a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5H20.25a.75.75 0 0 1 .75.75ZM5.25 12a.75.75 0 0 1-.75.75H3a.75.75 0 0 1 0-1.5h1.5a.75.75 0 0 1 .75.75Zm12.96 5.46a.75.75 0 0 1 0 1.06l-1.06 1.06a.75.75 0 1 1-1.06-1.06l1.06-1.06a.75.75 0 0 1 1.06 0ZM8.91 5.42a.75.75 0 0 1 0 1.06L7.85 7.54A.75.75 0 0 1 6.79 6.48l1.06-1.06a.75.75 0 0 1 1.06 0Zm9.17 1.06a.75.75 0 0 1-1.06 0L15.96 5.42A.75.75 0 1 1 17.02 4.36l1.06 1.06a.75.75 0 0 1 0 1.06ZM8.91 18.58a.75.75 0 0 1-1.06 0L6.79 17.52A.75.75 0 1 1 7.85 16.46l1.06 1.06a.75.75 0 0 1 0 1.06Z"
        />
      </svg>
    ),
  },
  {
    value: "dark",
    label: "Dark",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        <path
          fill="currentColor"
          d="M21.75 14.34a.75.75 0 0 0-.9-.96 7.5 7.5 0 0 1-9.23-9.23.75.75 0 0 0-.96-.9A9 9 0 1 0 21.75 14.34Z"
        />
      </svg>
    ),
  },
  {
    value: "system",
    label: "System",
    icon: (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        <path
          fill="currentColor"
          d="M3.75 5.25A2.25 2.25 0 0 1 6 3h12a2.25 2.25 0 0 1 2.25 2.25v9A2.25 2.25 0 0 1 18 16.5h-4.5v1.5H16.5a.75.75 0 0 1 0 1.5h-9a.75.75 0 0 1 0-1.5H10.5V16.5H6A2.25 2.25 0 0 1 3.75 14.25v-9ZM6 4.5a.75.75 0 0 0-.75.75v9c0 .414.336.75.75.75h12a.75.75 0 0 0 .75-.75v-9a.75.75 0 0 0-.75-.75H6Z"
        />
      </svg>
    ),
  },
];

type ThemeSelectorProps = {
  variant?: "default" | "header";
  className?: string;
};

const ThemeSelector = ({
  variant = "header",
  className = "",
}: ThemeSelectorProps) => {
  const { themeMode, setThemeMode, isThemeLocked } = useTheme();

  if (isThemeLocked) {
    return null;
  }

  return (
    <div
      className={`theme-selector theme-selector--${variant} ${className}`.trim()}
      role="group"
      aria-label="Appearance"
    >
      {OPTIONS.map(({ value, label, icon }) => {
        const isActive = themeMode === value;
        return (
          <button
            key={value}
            type="button"
            className={`theme-selector__btn ${
              isActive ? "theme-selector__btn--active" : ""
            }`}
            onClick={() => setThemeMode(value)}
            aria-pressed={isActive}
            title={label}
          >
            {icon}
            {variant === "default" ? <span>{label}</span> : null}
            {variant === "header" ? (
              <span className="theme-selector__sr-only">{label}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

export default ThemeSelector;
